<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { StepUploadListDataQueryVariables, StepUploadViewerDataQuery } from '@/generated/graphql'
import gql from 'graphql-tag'
import { computed, ref, watch } from 'vue'
import ViewImageStepUpload from '@/components/stepUpload/ViewImageStepUpload.vue'
import ViewVideoStepUpload from '@/components/stepUpload/ViewVideoStepUpload.vue'

const props = defineProps<{
  stepId: string
  maxImageWidth: number
  maxImageHeight: number
}>()

const fetchQuery = useQuery<StepUploadViewerDataQuery, StepUploadListDataQueryVariables>(
  gql`
    query StepUploadViewerData($stepId: ID!, $imageOptions: ImageOptions!) {
      product {
        step(id: $stepId) {
          uploads {
            ... on IStepUpload {
              id
              upload {
                id
                extension {
                  ... on ImageExtension {
                    url(options: $imageOptions)
                  }
                  ... on VideoExtension {
                    url
                  }
                }
              }
            }
            ... on ImageStepUpload {
              caption
            }
            ... on VideoStepUpload {
              caption
              fragmentStartInMillis
              fragmentEndInMillis
              fragmentLoop
              trimmedUpload {
                id
                extension {
                  ... on VideoExtension {
                    url
                  }
                }
              }
            }
          }
        }
      }
    }
  `,
  () => ({
    stepId: props.stepId,
    imageOptions: {
      height: props.maxImageHeight,
      width: props.maxImageWidth,
    },
  }),
)
const uploads = computed(() => fetchQuery.result.value?.product.step.uploads || [])

const tabsModel = ref<string>()
watch(uploads, (v) => {
  tabsModel.value = v[0]?.id
})

type LocalUpload = StepUploadViewerDataQuery['product']['step']['uploads'][0]
type LocalImageExtension = LocalUpload['upload']['extension'] & { __typename: 'ImageExtension' }
const asImageExtension = (upload: LocalUpload): LocalImageExtension => {
  return upload.upload.extension as LocalImageExtension
}
type LocalVideoExtension = LocalUpload['upload']['extension'] & { __typename: 'VideoExtension' }
const asVideoExtension = (upload: LocalUpload): LocalVideoExtension => {
  return upload.upload.extension as LocalVideoExtension
}
type LocalVideoStepUpload = Extract<LocalUpload, { __typename?: 'VideoStepUpload' }>
// A trimmed upload already starts and ends at the fragment, so its offsets no longer apply.
const asVideoPlayback = (upload: LocalVideoStepUpload) => {
  const trimmedExtension = upload.trimmedUpload?.extension as LocalVideoExtension | undefined
  if (!trimmedExtension) {
    return { src: asVideoExtension(upload).url, options: upload }
  }
  return {
    src: trimmedExtension.url,
    options: { ...upload, fragmentStartInMillis: undefined, fragmentEndInMillis: undefined },
  }
}
// The media fragment paints the frame at the configured start time as the thumbnail.
const videoThumbSrc = (upload: LocalVideoStepUpload) => {
  const { src, options } = asVideoPlayback(upload)
  const startInMillis = options.fragmentStartInMillis || 100
  return `${src}#t=${startInMillis / 1000}`
}
</script>

<template>
  <v-slide-group
    v-if="uploads.length > 1"
    v-model="tabsModel"
    class="filmroll"
    show-arrows
    center-active
    mandatory
  >
    <v-slide-group-item
      v-for="u in uploads"
      :key="u.id"
      v-slot="{ isSelected, toggle }"
      :value="u.id"
    >
      <button
        type="button"
        class="filmroll-thumb"
        :class="{ 'filmroll-thumb--selected': isSelected }"
        @click="toggle"
      >
        <v-img
          v-if="u.__typename == 'ImageStepUpload'"
          :src="asImageExtension(u).url"
          cover
          width="64"
          height="64"
        />
        <span v-else-if="u.__typename == 'VideoStepUpload'" class="filmroll-video">
          <!-- Same CORS mode as the player; a mode mismatch on a shared url poisons Chrome's media cache. -->
          <video :src="videoThumbSrc(u)" crossorigin="anonymous" preload="metadata" muted />
          <v-icon class="filmroll-video-icon" size="28">play_circle</v-icon>
        </span>
      </button>
    </v-slide-group-item>
  </v-slide-group>

  <v-window v-model="tabsModel">
    <v-window-item v-for="u in uploads" :key="u.id" :value="u.id" eager class="text-center">
      <view-image-step-upload
        v-if="u.__typename == 'ImageStepUpload'"
        :src="asImageExtension(u).url"
        :max-width="props.maxImageWidth"
        :max-height="props.maxImageHeight"
        :options="u"
        class="mx-auto mt-3"
      />
      <view-video-step-upload
        v-if="u.__typename == 'VideoStepUpload'"
        :src="asVideoPlayback(u).src"
        width="100%"
        height="100%"
        :options="asVideoPlayback(u).options"
      />
    </v-window-item>
  </v-window>
</template>

<style scoped lang="scss">
.filmroll {
  :deep(.v-slide-group__content) {
    justify-content: center;
    gap: 8px;
    padding: 4px 2px;
  }
}
.filmroll-thumb {
  width: 64px;
  height: 64px;
  flex: 0 0 auto;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  opacity: 0.7;

  &--selected {
    border-color: rgb(var(--v-theme-primary));
    opacity: 1;
  }
  &:hover {
    opacity: 1;
  }
}
.filmroll-video {
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  background: rgba(var(--v-theme-on-surface), 0.08);

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }
}
.filmroll-video-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.6));
}
</style>
